import {
  useEffect,
  useMemo,
  useRef,
  useState,
  type CSSProperties,
  type FormEvent,
  type KeyboardEvent,
} from 'react';
import { productConfig } from '../../config/product';
import { CONNECTIVE_CANVAS, CONNECTIVE_LAYER, CONNECTIVE_PARTIES } from './connectiveData';
import type { LandingIconName } from './icons';
import {
  CONTACT_DATA,
  FOOTER_COPY,
  FOOTER_LINK_COLUMNS,
  INTELLIGENCE_POINTS,
  LANDING_COPY,
  LANDING_MOBILE_NAV_LINKS,
  LANDING_NAV_LINKS,
  MODULE_CARDS,
  PARTNER_TILES,
  PROBLEM_FRAGMENTS,
  RESEARCH_TRUST_CARDS,
  SIGNAL_BARS,
  SIGNAL_PANEL,
  SOLUTION_CARDS,
  STICKY_CTA,
} from './landingData';
import { LOOP_STEPS, LOOP_ZONES, type LoopZoneKey } from './loopData';

const LOOP_CENTER = 100;
const LOOP_RADIUS = 70;
const LOOP_CORE = 42;
const LOOP_NODE_RADIUS = 14;
const LOOP_PACKET_RADIUS = 3.4;
const LOOP_LAP_MS = 16000;
const LOOP_TRAIL_LENGTH = 165;
const LOOP_DWELL = 0.45;
const LOOP_DWELL_AVG = 1 - 0.5 * LOOP_DWELL;
const LOOP_NODE_LEAD = (LOOP_NODE_RADIUS + 3) / LOOP_RADIUS / (Math.PI * 2);

type ConnectiveFocusKey = (typeof CONNECTIVE_PARTIES)[number]['key'] | 'rl' | null;

function cssVars(vars: Record<`--${string}`, string>): CSSProperties {
  return vars as CSSProperties;
}

function pad(index: number) {
  return String(index + 1).padStart(2, '0');
}

function stripHtml(value: string) {
  return value.replace(/<[^>]+>/g, '');
}

function usePrefersReducedMotion() {
  const [reduced, setReduced] = useState(false);

  useEffect(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return;
    const query = window.matchMedia('(prefers-reduced-motion: reduce)');
    setReduced(query.matches);
    const onChange = () => setReduced(query.matches);
    query.addEventListener?.('change', onChange);
    return () => query.removeEventListener?.('change', onChange);
  }, []);

  return reduced;
}

function useLandingEnhancements() {
  useEffect(() => {
    document.documentElement.classList.add('reveal-ready');

    const reveals = Array.from(document.querySelectorAll<HTMLElement>('.reveal'));
    const fragments = Array.from(document.querySelectorAll<HTMLElement>('#fragments .fragment'));
    const signalBars = Array.from(document.querySelectorAll<HTMLElement>('#signalPanel .bar-fill'));

    if (!('IntersectionObserver' in window)) {
      reveals.forEach((node) => node.classList.add('in'));
      fragments.forEach((node) => node.classList.add('solved'));
      signalBars.forEach((node) => {
        node.style.width = `${node.dataset.v ?? 0}%`;
      });
      return;
    }

    const revealObserver = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          entry.target.classList.add('in');
          revealObserver.unobserve(entry.target);
        });
      },
      { threshold: 0.12, rootMargin: '0px 0px -8% 0px' },
    );

    reveals.forEach((node) => revealObserver.observe(node));

    const sectionObserver = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          if (entry.target.id === 'fragments') {
            fragments.forEach((node, index) => {
              window.setTimeout(() => node.classList.add('solved'), 220 * index + 200);
            });
          }
          if (entry.target.id === 'signalPanel') {
            signalBars.forEach((node, index) => {
              window.setTimeout(
                () => {
                  node.style.width = `${node.dataset.v ?? 0}%`;
                },
                120 * index + 150,
              );
            });
          }
          sectionObserver.unobserve(entry.target);
        });
      },
      { threshold: 0.3 },
    );

    const fragmentList = document.getElementById('fragments');
    const signalPanel = document.getElementById('signalPanel');
    if (fragmentList) sectionObserver.observe(fragmentList);
    if (signalPanel) sectionObserver.observe(signalPanel);

    return () => {
      revealObserver.disconnect();
      sectionObserver.disconnect();
    };
  }, []);
}

function useForcedLightTheme() {
  useEffect(() => {
    document.documentElement.setAttribute('data-theme', 'light');
  }, []);
}

function IconPaths({ name }: { name: LandingIconName }) {
  switch (name) {
    case 'procure':
      return (
        <>
          <path d="M8.5 3h7" />
          <path d="M10 3v12.5a2 2 0 0 0 4 0V3" />
          <path d="M10 12.5h4" />
        </>
      );
    case 'produce':
    case 'lab':
      return (
        <>
          <path d="M9.5 3v5l-4.3 9a2 2 0 0 0 1.8 2.9h10a2 2 0 0 0 1.8-2.9L14.5 8V3" />
          <path d="M8.2 3h7.6M7.7 14.5h8.6" />
        </>
      );
    case 'purchase':
      return (
        <>
          <rect x="6" y="5" width="12" height="15" rx="1.7" />
          <path d="M9.2 5V4.3A1.3 1.3 0 0 1 10.5 3h3A1.3 1.3 0 0 1 14.8 4.3V5" />
          <path d="M9.3 12.8l2 2 3.4-3.9" />
        </>
      );
    case 'administer':
      return (
        <>
          <path d="M12 3.4c3 3.9 5.4 6.7 5.4 9.4a5.4 5.4 0 0 1-10.8 0c0-2.7 2.4-5.5 5.4-9.4z" />
          <path d="M9.8 13.2a2.2 2.2 0 0 0 2.2 2.2" />
        </>
      );
    case 'feedback':
    case 'improved':
      return (
        <>
          <circle cx="6.5" cy="16.5" r="2.2" />
          <circle cx="17.5" cy="16.5" r="2.2" />
          <circle cx="12" cy="6" r="2.2" />
          <path d="M8.3 15 10.7 8M15.7 15 13.3 8" />
        </>
      );
    case 'loop':
      return (
        <>
          <circle cx="12" cy="12" r="8.25" />
          <path d="M12 3.75a8.25 8.25 0 0 1 7.13 4.13" />
          <circle cx="12" cy="12" r="2.55" />
        </>
      );
    case 'patient':
      return (
        <>
          <circle cx="12" cy="8" r="3.2" />
          <path d="M5.5 19c0-3.6 2.9-6 6.5-6s6.5 2.4 6.5 6" />
        </>
      );
    case 'physician':
      return <path d="M3 12h3.5l2-6 4 12 2-6H21" />;
    case 'protocol':
      return (
        <>
          <rect x="5.5" y="3.5" width="13" height="17" rx="2" />
          <path d="M9 9h6M9 13h6M9 17h3.5" />
        </>
      );
    case 'outcomes':
      return (
        <>
          <path d="M4 4v16h16" />
          <path d="M7.5 15l3.2-3.8 3 2.4L20 7.5" />
        </>
      );
    case 'analysis':
      return (
        <>
          <rect x="7" y="7" width="10" height="10" rx="2" />
          <path d="M12 3.5v3.5M12 17v3.5M3.5 12H7M17 12h3.5" />
        </>
      );
    case 'bell':
      return (
        <>
          <path d="M6.5 9.5a5.5 5.5 0 0 1 11 0c0 4.5 2 5.5 2 5.5H4.5s2-1 2-5.5" />
          <path d="M10 19a2 2 0 0 0 4 0" />
        </>
      );
    case 'network':
      return (
        <>
          <circle cx="6" cy="6.5" r="2.2" />
          <circle cx="18" cy="6.5" r="2.2" />
          <circle cx="12" cy="18" r="2.2" />
          <path d="M7.6 8l3.5 8M16.4 8l-3.5 8M8.2 6.5h7.6" />
        </>
      );
    case 'dashboard':
      return (
        <>
          <rect x="4" y="4" width="16" height="16" rx="2" />
          <path d="M4 9.5h16M10 9.5V20" />
        </>
      );
    case 'shield':
      return <path d="M12 3l7 3v5c0 5-3 8.2-7 10-4-1.8-7-5-7-10V6z" />;
    case 'shieldck':
      return (
        <>
          <path d="M12 3l7 3v5c0 5-3 8.2-7 10-4-1.8-7-5-7-10V6z" />
          <path d="M9 11.8l2.2 2.2L15.2 10" />
        </>
      );
    case 'lock':
      return (
        <>
          <rect x="5" y="11" width="14" height="9" rx="2" />
          <path d="M8 11V8a4 4 0 0 1 8 0v3" />
        </>
      );
    case 'report':
      return (
        <>
          <rect x="5.5" y="3.5" width="13" height="17" rx="2" />
          <path d="M9 8h6M9 12h6M9 16h4" />
        </>
      );
    case 'labs':
      return (
        <>
          <path d="M9 3h6M10 3v6l-5 8.5A2 2 0 0 0 6.7 21h10.6a2 2 0 0 0 1.7-3.5L14 9V3" />
          <path d="M8 14h8" />
        </>
      );
    case 'phy':
      return (
        <>
          <path d="M12 3v4M9 5h6" />
          <path d="M7 7v4a5 5 0 0 0 10 0V7" />
          <circle cx="18" cy="16" r="3" />
          <path d="M12 16v-1" />
        </>
      );
    case 'pat':
      return (
        <path d="M20.8 6.6a4.6 4.6 0 0 0-7.8-2.1l-1 .9-1-.9A4.6 4.6 0 0 0 3.2 9c0 5 8.8 10.5 8.8 10.5S20.8 14 20.8 9z" />
      );
    case 'rl':
      return (
        <>
          <path
            d="M20.5 12a8.5 8.5 0 1 0-2.2 5.7"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.7"
            strokeLinecap="round"
          />
          <path
            d="M20.5 6.5v5h-5"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.7"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </>
      );
    default:
      return null;
  }
}

function Icon({ name, className }: { name: LandingIconName; className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <IconPaths name={name} />
    </svg>
  );
}

const REGENLOOP_ICON_SRC = '/brand/regenloop-icon.png';

function BrandMark() {
  return (
    <img className="mark" src={REGENLOOP_ICON_SRC} alt="" aria-hidden="true" draggable="false" />
  );
}

function BrandText({ includeSuffix = true }: { includeSuffix?: boolean }) {
  return (
    <span>
      <b>{LANDING_COPY.brand.prefix}</b>
      <span className="lp">{LANDING_COPY.brand.core}</span>
      {includeSuffix && <span className="dot-ai">{LANDING_COPY.brand.suffix}</span>}
    </span>
  );
}

function ArrowIcon() {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M5 12h14M13 6l6 6-6 6" />
    </svg>
  );
}

export function LandingNav() {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled((window.scrollY || 0) > 24);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => {
    document.body.style.overflow = mobileOpen ? 'hidden' : '';
    return () => {
      document.body.style.overflow = '';
    };
  }, [mobileOpen]);

  const closeMobile = () => setMobileOpen(false);

  return (
    <>
      <header className={`nav${scrolled ? ' scrolled' : ''}${mobileOpen ? ' open' : ''}`} id="nav">
        <div className="nav-inner">
          <a
            className="brand"
            href="#top"
            aria-label={LANDING_COPY.brand.homeLabel}
            onClick={closeMobile}
          >
            <BrandText />
          </a>
          <nav className="nav-links" aria-label="Primary">
            {LANDING_NAV_LINKS.map((item) => (
              <a key={item.href} href={item.href}>
                {item.label}
              </a>
            ))}
          </nav>
          <div className="nav-cta">
            <a href="#contact" className="btn btn-primary">
              Request a Briefing
            </a>
            <button
              className="nav-toggle"
              id="navToggle"
              type="button"
              aria-label="Open menu"
              aria-expanded={mobileOpen}
              onClick={() => setMobileOpen((open) => !open)}
            >
              <span />
            </button>
          </div>
        </div>
      </header>

      <div className={`mobile-menu${mobileOpen ? ' open' : ''}`} id="mobileMenu">
        {LANDING_MOBILE_NAV_LINKS.map((item) => (
          <a
            key={item.href}
            href={item.href}
            className={item.href === '#contact' ? 'btn btn-primary mm-cta' : undefined}
            onClick={closeMobile}
          >
            {item.label}
          </a>
        ))}
      </div>
    </>
  );
}

function polar(center: number, radius: number, degrees: number) {
  const angle = (degrees * Math.PI) / 180;
  return [center + radius * Math.cos(angle), center + radius * Math.sin(angle)] as const;
}

function arcPath(center: number, radius: number, startDegrees: number, endDegrees: number) {
  const [x0, y0] = polar(center, radius, startDegrees);
  const [x1, y1] = polar(center, radius, endDegrees);
  const large = endDegrees - startDegrees > 180 ? 1 : 0;
  return `M${x0.toFixed(2)} ${y0.toFixed(2)} A ${radius} ${radius} 0 ${large} 1 ${x1.toFixed(2)} ${y1.toFixed(2)}`;
}

function zoneBands() {
  const order: LoopZoneKey[] = [];
  LOOP_STEPS.forEach((step) => {
    if (!order.includes(step.zone)) order.push(step.zone);
  });

  return order.map((zone) => {
    const indices = LOOP_STEPS.map((step, index) => (step.zone === zone ? index : -1)).filter(
      (index) => index >= 0,
    );
    const firstIndex = indices[0] ?? 0;
    const lastIndex = indices[indices.length - 1] ?? firstIndex;
    const per = 360 / LOOP_STEPS.length;
    const start = -90 + firstIndex * per - per / 2 + 4.5;
    const end = -90 + lastIndex * per + per / 2 - 4.5;
    return { zone, path: arcPath(LOOP_CENTER, LOOP_RADIUS, start, end) };
  });
}

function loopPoint(index: number) {
  const fraction = index / LOOP_STEPS.length;
  return loopProgressPoint(fraction);
}

function loopProgressPoint(fraction: number) {
  const angle = (-90 + fraction * 360) * (Math.PI / 180);
  return {
    x: LOOP_CENTER + LOOP_RADIUS * Math.cos(angle),
    y: LOOP_CENTER + LOOP_RADIUS * Math.sin(angle),
    angle,
    fraction,
  };
}

function LoopCenter({
  activeIndex,
  state,
  ids,
}: {
  activeIndex: number;
  state: 'brand' | 'stage';
  ids: { tag: string; idx: string; name: string };
}) {
  const activeStep = LOOP_STEPS[activeIndex] ?? LOOP_STEPS[0];
  const activeZone = LOOP_ZONES[activeStep.zone];

  return (
    <div
      className="loop-center"
      id={ids.name === 'lcName' ? 'loopCenter' : 'bigCenter'}
      data-state={state}
      aria-live="polite"
    >
      <div className="lc-brand">
        <div className="lc-word">
          <BrandText />
        </div>
        <div className="lc-cap">CLOSED-LOOP</div>
      </div>
      <div className="lc-stage">
        <div className="lc-tag" id={ids.tag} style={cssVars({ '--zone': activeZone.color })}>
          {activeStep.tag}
        </div>
        <div className="lc-idx" id={ids.idx}>
          {pad(activeIndex)}
        </div>
        <div className="lc-name" id={ids.name}>
          {activeStep.label}
        </div>
      </div>
    </div>
  );
}

export function LoopDiagram({ variant }: { variant: 'hero' | 'big' }) {
  const reducedMotion = usePrefersReducedMotion();
  const [activeIndex, setActiveIndex] = useState(0);
  const [engaged, setEngaged] = useState(false);
  const [pinnedIndex, setPinnedIndex] = useState<number | null>(null);
  const bands = useMemo(zoneBands, []);
  const activeStep = LOOP_STEPS[activeIndex] ?? LOOP_STEPS[0];
  const activeZone = LOOP_ZONES[activeStep.zone];
  const centerState = engaged || pinnedIndex !== null ? 'stage' : 'brand';
  const packetRef = useRef<SVGCircleElement>(null);
  const rippleRef = useRef<SVGCircleElement>(null);
  const progressRef = useRef<SVGCircleElement>(null);
  const positionRef = useRef(0);
  const targetRef = useRef<number | null>(null);
  const rafRef = useRef(0);
  const lastTimeRef = useRef(0);
  const lastNodeRef = useRef(-1);
  const activeIndexRef = useRef(activeIndex);
  const svgId = variant === 'hero' ? 'heroLoopA' : 'bigLoopA';
  const stageId = variant === 'hero' ? 'loopStage' : 'bigStage';
  const readoutId = variant === 'hero' ? 'loopReadout' : 'bigReadout';
  const stepperId = variant === 'hero' ? 'loopStepper' : 'bigStepper';
  const centerIds =
    variant === 'hero'
      ? { tag: 'lcTag', idx: 'lcIdx', name: 'lcName' }
      : { tag: 'bgTag', idx: 'bgIdx', name: 'bgName' };
  const readoutIds =
    variant === 'hero'
      ? {
          no: 'lrNo',
          name: 'lrName',
          zone: 'lrZone',
          stat: 'lrStat',
          statLabel: 'lrStatL',
          note: 'lrNote',
        }
      : {
          no: 'brNo',
          name: 'brName',
          zone: 'brZone',
          stat: 'brStat',
          statLabel: 'brStatL',
          note: 'brNote',
        };

  const paintPacket = (fraction: number, comet: boolean) => {
    const normalized = ((fraction % 1) + 1) % 1;
    const packetPosition = loopProgressPoint(normalized);
    packetRef.current?.setAttribute('cx', packetPosition.x.toFixed(2));
    packetRef.current?.setAttribute('cy', packetPosition.y.toFixed(2));

    if (comet) {
      progressRef.current?.setAttribute(
        'stroke-dasharray',
        `${LOOP_TRAIL_LENGTH} ${1000 - LOOP_TRAIL_LENGTH}`,
      );
      progressRef.current?.setAttribute(
        'stroke-dashoffset',
        (LOOP_TRAIL_LENGTH - normalized * 1000).toFixed(1),
      );
    } else {
      progressRef.current?.setAttribute(
        'stroke-dasharray',
        `${(normalized * 1000).toFixed(1)} 1000`,
      );
      progressRef.current?.setAttribute('stroke-dashoffset', '0');
    }
  };

  const fireRipple = (index: number) => {
    const point = loopPoint(index);
    const ripple = rippleRef.current;
    if (!ripple) return;
    ripple.setAttribute('cx', point.x.toFixed(2));
    ripple.setAttribute('cy', point.y.toFixed(2));
    ripple.classList.remove('go');
    void ripple.getBoundingClientRect();
    ripple.classList.add('go');
  };

  const activateStep = (index: number) => {
    activeIndexRef.current = index;
    setActiveIndex(index);
    fireRipple(index);
  };

  useEffect(() => {
    activeIndexRef.current = activeIndex;
  }, [activeIndex]);

  useEffect(() => {
    if (reducedMotion) {
      cancelAnimationFrame(rafRef.current);
      lastTimeRef.current = 0;
      targetRef.current = null;
      positionRef.current = activeIndexRef.current / LOOP_STEPS.length;
      paintPacket(positionRef.current, false);
      return;
    }

    lastNodeRef.current = activeIndexRef.current;
    const lapSpeed = 1 / LOOP_LAP_MS;

    function tick(time: number) {
      const dt = lastTimeRef.current ? Math.min(time - lastTimeRef.current, 80) : 0;
      lastTimeRef.current = time;

      if (targetRef.current !== null) {
        let delta = targetRef.current - positionRef.current;
        if (delta > 0.5) delta -= 1;
        else if (delta < -0.5) delta += 1;

        if (Math.abs(delta) < 0.0015) {
          positionRef.current = targetRef.current;
          targetRef.current = null;
        } else {
          positionRef.current += delta * Math.min(1, dt / 90);
        }
        if (positionRef.current < 0) positionRef.current += 1;
        else if (positionRef.current >= 1) positionRef.current -= 1;
        paintPacket(positionRef.current, false);
      } else if (engaged || pinnedIndex !== null) {
        paintPacket(positionRef.current, false);
      } else {
        const segment = (positionRef.current * LOOP_STEPS.length) % 1;
        const dwell = Math.cos(Math.PI * segment);
        const speed = (1 - LOOP_DWELL * dwell * dwell) / LOOP_DWELL_AVG;
        positionRef.current += dt * lapSpeed * speed;
        if (positionRef.current >= 1) positionRef.current -= 1;
        paintPacket(positionRef.current, true);

        const nextNode =
          Math.floor((positionRef.current + LOOP_NODE_LEAD) * LOOP_STEPS.length) %
          LOOP_STEPS.length;
        if (nextNode !== lastNodeRef.current) {
          lastNodeRef.current = nextNode;
          activateStep(nextNode);
        }
      }

      rafRef.current = requestAnimationFrame(tick);
    }

    rafRef.current = requestAnimationFrame(tick);
    return () => {
      cancelAnimationFrame(rafRef.current);
      lastTimeRef.current = 0;
    };
  }, [engaged, pinnedIndex, reducedMotion]);

  const engage = (index: number) => {
    setEngaged(true);
    targetRef.current = index / LOOP_STEPS.length;
    activateStep(index);
  };

  const release = () => {
    if (pinnedIndex === null) {
      targetRef.current = null;
      setEngaged(false);
    }
  };

  const pin = (index: number) => {
    if (pinnedIndex === index) {
      targetRef.current = null;
      setPinnedIndex(null);
      setEngaged(false);
      return;
    }

    targetRef.current = index / LOOP_STEPS.length;
    setEngaged(true);
    setPinnedIndex(index);
    activateStep(index);
  };

  const keyPin = (event: KeyboardEvent<SVGCircleElement>, index: number) => {
    if (event.key !== 'Enter' && event.key !== ' ') return;
    event.preventDefault();
    pin(index);
  };

  return (
    <>
      <div
        className={`loop-stage loop-interactive${engaged ? ' is-engaged' : ''}${pinnedIndex !== null ? ' is-pinned' : ''}`}
        id={stageId}
        onMouseLeave={release}
        style={cssVars({ '--zone': activeZone.color })}
      >
        <svg
          className={activeStep.label === 'Learn' ? 'loop-svg is-feedback' : 'loop-svg'}
          id={svgId}
          viewBox="0 0 200 200"
          aria-label="Interactive closed-loop data diagram — explore each stage"
        >
          <circle cx={LOOP_CENTER} cy={LOOP_CENTER} r={LOOP_RADIUS} className="loop-ring" />
          {bands.map((band) => (
            <path
              key={band.zone}
              data-zone={band.zone}
              className="loop-zone-band"
              d={band.path}
              style={{ stroke: LOOP_ZONES[band.zone].color }}
            />
          ))}
          <circle
            ref={progressRef}
            cx={LOOP_CENTER}
            cy={LOOP_CENTER}
            r={LOOP_RADIUS}
            className="loop-progress"
            pathLength="1000"
            transform={`rotate(-90 ${LOOP_CENTER} ${LOOP_CENTER})`}
            strokeDasharray="0 1000"
            strokeDashoffset="0"
          />
          <circle
            cx={LOOP_CENTER}
            cy={LOOP_CENTER}
            r={LOOP_CORE}
            className="loop-core loop-core-i"
          />
          {LOOP_STEPS.map((_, index) => {
            const point = loopPoint(index);
            const x1 = LOOP_CENTER + LOOP_CORE * Math.cos(point.angle);
            const y1 = LOOP_CENTER + LOOP_CORE * Math.sin(point.angle);
            const x2 = LOOP_CENTER + (LOOP_RADIUS - LOOP_NODE_RADIUS) * Math.cos(point.angle);
            const y2 = LOOP_CENTER + (LOOP_RADIUS - LOOP_NODE_RADIUS) * Math.sin(point.angle);
            return (
              <line
                key={`spoke-${index}`}
                x1={x1.toFixed(2)}
                y1={y1.toFixed(2)}
                x2={x2.toFixed(2)}
                y2={y2.toFixed(2)}
                className="loop-spoke"
                data-i={index}
              />
            );
          })}
          <circle
            ref={rippleRef}
            cx={loopPoint(0).x.toFixed(2)}
            cy={loopPoint(0).y.toFixed(2)}
            r={LOOP_NODE_RADIUS}
            className="loop-ripple"
          />
          <circle
            ref={packetRef}
            cx={loopPoint(0).x.toFixed(2)}
            cy={loopPoint(0).y.toFixed(2)}
            r={LOOP_PACKET_RADIUS}
            className="loop-packet"
          />
          {LOOP_STEPS.map((step, index) => {
            const point = loopPoint(index);
            const iconScale = 0.6;
            const pointX = point.x.toFixed(2);
            const pointY = point.y.toFixed(2);
            const entryDegrees =
              (Math.atan2(-Math.cos(point.angle), Math.sin(point.angle)) * 180) / Math.PI;
            return (
              <g
                key={step.label}
                className={`loop-node${index === activeIndex ? ' is-on' : ''}`}
                data-i={index}
              >
                <circle cx={pointX} cy={pointY} r={LOOP_NODE_RADIUS} className="dot" />
                <circle
                  cx={pointX}
                  cy={pointY}
                  r={LOOP_NODE_RADIUS}
                  className="dot-ring"
                  pathLength="100"
                  transform={`rotate(${entryDegrees.toFixed(1)} ${pointX} ${pointY})`}
                />
                <g
                  transform={`translate(${(point.x - 12 * iconScale).toFixed(2)},${(point.y - 12 * iconScale).toFixed(2)}) scale(${iconScale})`}
                >
                  <g className="ico" stroke="currentColor" fill="none">
                    <IconPaths name={step.icon} />
                  </g>
                </g>
                <circle
                  cx={pointX}
                  cy={pointY}
                  r={LOOP_NODE_RADIUS * 1.7}
                  className="loop-hit"
                  tabIndex={0}
                  role="button"
                  aria-label={`${step.label} — ${step.tag}`}
                  onMouseEnter={() => engage(index)}
                  onFocus={() => engage(index)}
                  onBlur={release}
                  onClick={() => pin(index)}
                  onKeyDown={(event) => keyPin(event, index)}
                />
              </g>
            );
          })}
        </svg>
        <LoopCenter activeIndex={activeIndex} state={centerState} ids={centerIds} />
      </div>

      <div className="loop-readout" id={readoutId}>
        <div className="lr-line">
          <span className="lr-live" aria-hidden="true" />
          <span className="lr-no" id={readoutIds.no}>
            {pad(activeIndex)}
          </span>
          <span className="lr-name" id={readoutIds.name}>
            {activeStep.label}
          </span>
          <span
            className="lr-zone"
            id={readoutIds.zone}
            style={cssVars({ '--zc': activeZone.color })}
          >
            {activeZone.name}
          </span>
          <span className="lr-stat">
            <b id={readoutIds.stat}>{activeStep.stat}</b>{' '}
            <span id={readoutIds.statLabel}>{activeStep.statLabel}</span>
          </span>
        </div>
        <p className="lr-note tick" id={readoutIds.note}>
          {activeStep.note}
        </p>
        <div
          className="loop-stepper"
          id={stepperId}
          role="tablist"
          aria-label="Loop stages"
          onMouseLeave={release}
        >
          {LOOP_STEPS.map((step, index) => (
            <button
              key={step.label}
              className={`lstep${index === activeIndex ? ' is-on' : ''}${pinnedIndex === index ? ' is-pinned' : ''}`}
              type="button"
              role="tab"
              aria-label={`${step.label} — ${step.tag}`}
              aria-selected={index === activeIndex}
              onMouseEnter={() => engage(index)}
              onFocus={() => engage(index)}
              onBlur={release}
              onClick={() => pin(index)}
            >
              <span className="ls-n">{pad(index)}</span>
            </button>
          ))}
        </div>
      </div>
    </>
  );
}

export function Hero() {
  return (
    <section className="s-dark hero grid-bg heroA" data-screen-label="Hero">
      <div className="wrap hero-inner">
        <div className="hero-copy">
          <span className="eyebrow reveal">{LANDING_COPY.hero.eyebrow}</span>
          <h1 className="display reveal d1">
            {LANDING_COPY.hero.headingLead}{' '}
            <span className="accent-line">{LANDING_COPY.hero.headingAccent}</span>
          </h1>
          <p className="lede reveal d2">{LANDING_COPY.hero.body}</p>
          <div className="hero-cta reveal d3">
            <a href="#contact" className="btn btn-primary">
              Request a Briefing
              <ArrowIcon />
            </a>
            <a href="#loop" className="btn btn-ghost">
              See the Loop
            </a>
          </div>
        </div>
        <div className="hero-visual reveal d2">
          <LoopDiagram variant="hero" />
        </div>
      </div>
    </section>
  );
}

export function ClinicianAppShowcase() {
  return (
    <section
      className="s-dark section-pad grid-bg rl-mobile"
      id="app"
      data-screen-label="Clinician app"
    >
      <div className="wrap">
        <div className="head reveal" style={{ maxWidth: 780 }}>
          <span className="eyebrow">{LANDING_COPY.app.eyebrow}</span>
          <h2 className="h2">{LANDING_COPY.app.heading}</h2>
          <p>{LANDING_COPY.app.body}</p>
        </div>

        <div className="rl-composite reveal d1">
          <div className="rl-browser">
            <div className="rl-browser-bar">
              <span className="rl-dot" />
              <span className="rl-dot" />
              <span className="rl-dot" />
              <span className="rl-url">app.regenloop.ai/command-center</span>
            </div>
            <div className="rl-shot-stage">
              <img
                className="rl-shot"
                src="/images/console.png"
                width="1440"
                height="900"
                alt="RegenLoop desktop console showing command center, outcomes integrity, efficacy signals, and loop activity"
                loading="lazy"
              />
            </div>
          </div>
          <div className="rl-phone rl-phone--float">
            <span className="rl-notch" />
            <div className="rl-screen">
              <img
                className="rl-shot"
                src="/images/today.png"
                width="249"
                height="540"
                alt="RegenLoop clinician mobile app Today screen"
                loading="lazy"
              />
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

function SectionHead({
  eyebrow,
  heading,
  body,
  centered = false,
  accent,
}: {
  eyebrow: string;
  heading: string;
  body?: string;
  centered?: boolean;
  accent?: boolean;
}) {
  return (
    <div
      className="head reveal"
      style={centered ? { marginInline: 'auto', textAlign: 'center' } : undefined}
    >
      <span
        className="eyebrow"
        style={{
          justifyContent: centered ? 'center' : undefined,
          color: accent ? 'var(--violet-ink, var(--violet))' : undefined,
        }}
      >
        {eyebrow}
      </span>
      <h2 className="h2" style={accent ? { marginTop: 18 } : undefined}>
        {heading}
      </h2>
      {body && <p style={centered ? { marginInline: 'auto' } : undefined}>{body}</p>}
    </div>
  );
}

export function ProblemFragments() {
  return (
    <section className="s-dark section-pad" id="problem" data-screen-label="Problem">
      <div className="wrap">
        <div className="problem-grid">
          <SectionHead
            eyebrow={LANDING_COPY.problem.eyebrow}
            heading={LANDING_COPY.problem.heading}
            body={LANDING_COPY.problem.body}
          />
          <div className="fragment-list reveal d1" id="fragments">
            {PROBLEM_FRAGMENTS.map((text) => (
              <div className="fragment" key={text}>
                <svg
                  className="x"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="1.6"
                  strokeLinecap="round"
                  aria-hidden="true"
                >
                  <path d="M7 7l10 10M17 7L7 17" />
                </svg>{' '}
                {text}
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

export function CardGrid() {
  return (
    <section className="s-light section-pad" id="platform" data-screen-label="Solution">
      <div className="wrap">
        <SectionHead
          eyebrow={LANDING_COPY.solution.eyebrow}
          heading={LANDING_COPY.solution.heading}
          body={LANDING_COPY.solution.body}
        />
        <div className="card-grid cols-3" id="solutionGrid">
          {SOLUTION_CARDS.map((item, index) => (
            <div className={`card reveal d${(index % 3) + 1}`} key={item.title}>
              <div className="ic">
                <Icon name={item.icon} />
              </div>
              <h3>{item.title}</h3>
              <p>{item.body}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function connectivePathClass(id: string, focusKey: ConnectiveFocusKey) {
  if (!focusKey) return '';
  if (focusKey === 'rl') return ' lit';
  if (id === `c-${focusKey}` || (focusKey === 'labs' && id === 'fb')) return ' lit';
  if (id === 'h1' && focusKey === 'phy') return ' lit';
  if (id === 'h2' && focusKey === 'pat') return ' lit';
  return '';
}

function ConnectiveReadout({ focusKey }: { focusKey: ConnectiveFocusKey }) {
  if (focusKey === 'labs') {
    return (
      <>
        <b>Labs</b> source and release the stem-cell lines. RegenLoop logs every line and lot at the
        point of origin, then returns structured intelligence that sharpens what they source next.
      </>
    );
  }

  if (focusKey === 'phy') {
    return (
      <>
        <b>Physicians</b> acquire and administer the lines. Every dose is captured as structured,
        comparable data, not free-text notes, and they get protocol-level review cues back.
      </>
    );
  }

  if (focusKey === 'pat') {
    return (
      <>
        <b>Patients</b> generate the outcomes. Standardized, longitudinal follow-up turns lived
        results into the signal that drives the entire loop.
      </>
    );
  }

  if (focusKey === 'rl') {
    return (
      <>
        <b>RegenLoop is the connective layer.</b> It captures structured data from all three
        parties, links every outcome to the exact line and dose behind it, and feeds review signals
        back to sharpen the next line and protocol.
      </>
    );
  }

  return (
    <span className="hint">
      Three parties, one connective layer.{' '}
      <b style={{ color: 'var(--ink-on-dark)' }}>Hover any party</b> to see what it gives the loop
      and what it gets back.
    </span>
  );
}

export function ConnectiveLayer() {
  const stageRef = useRef<HTMLDivElement | null>(null);
  const reducedMotion = usePrefersReducedMotion();
  const [scale, setScale] = useState(1);
  const [focusKey, setFocusKey] = useState<ConnectiveFocusKey>(null);

  useEffect(() => {
    const stage = stageRef.current;
    if (!stage) return;
    const fit = () => {
      const width = stage.clientWidth || CONNECTIVE_CANVAS.width;
      setScale(Math.min(1, width / CONNECTIVE_CANVAS.width));
    };
    fit();
    if (typeof ResizeObserver === 'undefined') {
      window.addEventListener('resize', fit);
      return () => window.removeEventListener('resize', fit);
    }
    const observer = new ResizeObserver(fit);
    observer.observe(stage);
    return () => observer.disconnect();
  }, []);

  const paths = [
    {
      id: 'h1',
      d: `M${200 + CONNECTIVE_CANVAS.halfCardWidth} ${CONNECTIVE_CANVAS.handoffY} L${560 - CONNECTIVE_CANVAS.halfCardWidth} ${CONNECTIVE_CANVAS.handoffY}`,
      className: 'handoff',
      marker: 'tArrow',
      keyName: 'phy',
    },
    {
      id: 'h2',
      d: `M${560 + CONNECTIVE_CANVAS.halfCardWidth} ${CONNECTIVE_CANVAS.handoffY} L${920 - CONNECTIVE_CANVAS.halfCardWidth} ${CONNECTIVE_CANVAS.handoffY}`,
      className: 'handoff',
      marker: 'tArrow',
      keyName: 'pat',
    },
    ...CONNECTIVE_PARTIES.map((party) => ({
      id: `c-${party.key}`,
      d: `M${party.cx} ${CONNECTIVE_CANVAS.connectorTop} L${party.cx} ${CONNECTIVE_CANVAS.layerTop}`,
      className: 'capture',
      marker: 'tArrowDim',
      keyName: party.key,
    })),
    {
      id: 'fb',
      d: `M${CONNECTIVE_CANVAS.layerLeft} ${CONNECTIVE_CANVAS.layerMid} C 22 ${CONNECTIVE_CANVAS.layerMid} 22 ${CONNECTIVE_CANVAS.handoffY} ${200 - CONNECTIVE_CANVAS.halfCardWidth} ${CONNECTIVE_CANVAS.handoffY}`,
      className: 'feedback',
      marker: 'tArrow',
      keyName: 'labs',
    },
  ];

  return (
    <section
      className="s-dark section-pad tissue-section grid-bg"
      id="connective"
      data-screen-label="Connective layer"
    >
      <div className="wrap">
        <SectionHead
          eyebrow={LANDING_COPY.connective.eyebrow}
          heading={LANDING_COPY.connective.heading}
          body={LANDING_COPY.connective.body}
          centered
        />
        <div
          className="tissue-stage reveal d1"
          id="tissueStage"
          ref={stageRef}
          style={{ height: CONNECTIVE_CANVAS.height * scale }}
        >
          <div
            className={`tissue-canvas${focusKey ? ' is-focus' : ''}`}
            id="tissueCanvas"
            role="group"
            aria-label="How RegenLoop connects labs, physicians, and patients through one intelligence layer"
            onMouseLeave={() => setFocusKey(null)}
            onBlur={(event) => {
              if (!event.currentTarget.contains(event.relatedTarget)) setFocusKey(null);
            }}
            style={{
              transform: `scale(${scale})`,
              left: (CONNECTIVE_CANVAS.width - CONNECTIVE_CANVAS.width * scale) / -2,
            }}
          >
            <svg
              className="tissue-links"
              viewBox={`0 0 ${CONNECTIVE_CANVAS.width} ${CONNECTIVE_CANVAS.height}`}
              width={CONNECTIVE_CANVAS.width}
              height={CONNECTIVE_CANVAS.height}
            >
              <defs>
                <marker
                  id="tArrow"
                  viewBox="0 0 10 10"
                  refX="8"
                  refY="5"
                  markerWidth="7"
                  markerHeight="7"
                  orient="auto-start-reverse"
                >
                  <path d="M0 0L10 5L0 10z" className="tissue-arrow" />
                </marker>
                <marker
                  id="tArrowDim"
                  viewBox="0 0 10 10"
                  refX="8"
                  refY="5"
                  markerWidth="6"
                  markerHeight="6"
                  orient="auto-start-reverse"
                >
                  <path d="M0 0L10 5L0 10z" className="tissue-arrow dim" />
                </marker>
              </defs>
              {paths.map((path) => (
                <path
                  key={path.id}
                  id={`tp-${path.id}`}
                  d={path.d}
                  className={`tl-base ${path.className}${connectivePathClass(path.id, focusKey)}`}
                  markerEnd={`url(#${path.marker})`}
                  style={
                    focusKey
                      ? cssVars({
                          '--lc':
                            focusKey === 'rl'
                              ? 'var(--accent)'
                              : (CONNECTIVE_PARTIES.find((party) => party.key === focusKey)
                                  ?.color ?? 'var(--accent)'),
                        })
                      : undefined
                  }
                />
              ))}
              {!reducedMotion &&
                paths.flatMap((path) =>
                  Array.from(
                    { length: path.className === 'capture' ? 3 : path.id === 'fb' ? 3 : 2 },
                    (_, index) => (
                      <circle
                        key={`${path.id}-${index}`}
                        r={path.className === 'capture' ? '1.7' : '2.3'}
                        className={`tl-dot ${path.className === 'capture' ? 'cap' : path.id === 'fb' ? 'fb' : 'hand'}${focusKey === 'rl' || focusKey === path.keyName ? ' lit' : ''}`}
                        data-key={path.keyName}
                      >
                        <animateMotion
                          dur={`${path.id === 'fb' ? 5 : path.className === 'capture' ? 4.4 : 3.2}s`}
                          repeatCount="indefinite"
                          rotate="0"
                          path={path.d}
                          begin={`${-(index + 1)}s`}
                        />
                      </circle>
                    ),
                  ),
                )}
            </svg>
            <div className="handoff-lbl" style={{ left: 380, top: CONNECTIVE_CANVAS.handoffY }}>
              stem-cell lines
            </div>
            <div className="handoff-lbl" style={{ left: 740, top: CONNECTIVE_CANVAS.handoffY }}>
              treatment
            </div>
            {CONNECTIVE_PARTIES.map((party) => (
              <div
                key={party.key}
                className={`party${focusKey === 'rl' || focusKey === party.key ? ' lit' : ''}`}
                style={{
                  left: party.cx,
                  top: CONNECTIVE_CANVAS.cardTop,
                  ...cssVars({ '--pc': party.color }),
                }}
                tabIndex={0}
                role="button"
                aria-label={`Highlight ${party.name} in the loop`}
                data-key={party.key}
                onClick={() => setFocusKey(party.key)}
                onMouseEnter={() => setFocusKey(party.key)}
                onFocus={() => setFocusKey(party.key)}
                onKeyDown={(event) => {
                  if (event.key === 'Enter' || event.key === ' ') {
                    event.preventDefault();
                    setFocusKey(party.key);
                  }
                }}
              >
                <div className="p-top">
                  <span className="p-mk">
                    <Icon name={party.icon} />
                  </span>
                  <div>
                    <div className="p-role">{party.role}</div>
                    <div className="p-name">{party.name}</div>
                  </div>
                </div>
                <div className="p-stat">
                  <span className="v">{party.statValue}</span>
                  <span className="l">{party.statLabel}</span>
                </div>
              </div>
            ))}
            <div
              className={`tissue-layer${focusKey ? ' lit' : ''}`}
              tabIndex={0}
              role="button"
              aria-label="Highlight the RegenLoop connective layer"
              data-key="rl"
              onClick={() => setFocusKey('rl')}
              onMouseEnter={() => setFocusKey('rl')}
              onFocus={() => setFocusKey('rl')}
              onKeyDown={(event) => {
                if (event.key === 'Enter' || event.key === ' ') {
                  event.preventDefault();
                  setFocusKey('rl');
                }
              }}
            >
              <div className="tl-brand">
                <span className="glyph">
                  <Icon name={CONNECTIVE_LAYER.icon} />
                </span>
                <div>
                  <div className="tl-name">
                    {CONNECTIVE_LAYER.name}
                    <span className="ai">{CONNECTIVE_LAYER.suffix}</span>
                  </div>
                  <div className="tl-role">{CONNECTIVE_LAYER.role}</div>
                </div>
              </div>
              <div className="tl-pills">
                <span className="tl-pill">
                  Captures <b>every party</b>
                </span>
                <span className="tl-pill">
                  Links <b>outcome → line</b>
                </span>
                <span className="tl-pill">
                  <b>7</b> review signals
                </span>
              </div>
            </div>
          </div>
        </div>
        <div className="tissue-readout reveal d1" id="tissueReadout" aria-live="polite">
          <p className="rd tick" id="tissueRead">
            <ConnectiveReadout focusKey={focusKey} />
          </p>
        </div>
        <div className="tissue-fallback" id="tissueFallback">
          {[
            ...CONNECTIVE_PARTIES,
            {
              ...CONNECTIVE_LAYER,
              key: 'rl',
              color: 'var(--accent)',
              readoutHtml: CONNECTIVE_LAYER.mobileReadout,
              role: CONNECTIVE_LAYER.role,
              icon: CONNECTIVE_LAYER.icon,
            },
          ].map((item) => (
            <div className="tf-step" key={item.key} style={cssVars({ '--rc': item.color })}>
              <div className="tf-rail">
                <div className="tf-dot">
                  <Icon name={item.icon} />
                </div>
                <div className="tf-line" />
              </div>
              <div className="tf-b">
                <span className="role">{item.role}</span>
                <h3>{item.name}</h3>
                <p>{stripHtml(item.readoutHtml)}</p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

export function LoopSection() {
  return (
    <section
      className="s-dark section-pad loop-section grid-bg"
      id="loop"
      data-screen-label="The Loop"
    >
      <div
        className="glow"
        style={{
          width: 600,
          height: 600,
          background: 'var(--accent)',
          left: '50%',
          top: '40%',
          transform: 'translate(-50%,-50%)',
          opacity: 0.12,
        }}
      />
      <div className="wrap">
        <SectionHead
          eyebrow={LANDING_COPY.loop.eyebrow}
          heading={LANDING_COPY.loop.heading}
          body={LANDING_COPY.loop.body}
          centered
        />
        <div className="loop-big-stage reveal d1">
          <LoopDiagram variant="big" />
        </div>
        <ul className="loop-legend" id="loopLegend" aria-hidden="true">
          {Object.values(LOOP_ZONES).map((zone) => (
            <li className="lg-item" key={zone.key} style={cssVars({ '--zone': zone.color })}>
              <span className="lg-dot" />
              <span className="lg-name">{zone.name}</span>
              <span className="lg-stages">
                {LOOP_STEPS.filter((step) => step.zone === zone.key)
                  .map((step) => step.label)
                  .join(' · ')}
              </span>
            </li>
          ))}
        </ul>
        <ol className="loop-timeline" id="loopTimeline">
          {LOOP_STEPS.map((step, index) => (
            <li
              className="tl-item"
              key={step.label}
              style={cssVars({ '--zone': LOOP_ZONES[step.zone].color })}
            >
              <div className="tl-rail">
                <span className="tl-dot">
                  <Icon name={step.icon} />
                </span>
                <span className="tl-line" />
              </div>
              <div className="tl-body">
                <span className="tl-step">
                  Step {pad(index)} · {LOOP_ZONES[step.zone].name}
                </span>
                <h3>{step.label}</h3>
                <p>{step.note}</p>
              </div>
            </li>
          ))}
        </ol>
      </div>
    </section>
  );
}

export function ModulesGrid() {
  return (
    <section className="s-light section-pad" id="modules" data-screen-label="Platform modules">
      <div className="wrap">
        <SectionHead
          eyebrow={LANDING_COPY.modules.eyebrow}
          heading={LANDING_COPY.modules.heading}
          body={LANDING_COPY.modules.body}
        />
        <div className="card-grid cols-3 module-grid" id="moduleGrid">
          {MODULE_CARDS.map((item, index) => (
            <div className={`card card--module reveal d${(index % 3) + 1}`} key={item.title}>
              <span className="num">{pad(index)}</span>
              <div className="ic">
                <Icon name={item.icon} />
              </div>
              <h3>{item.title}</h3>
              <p>{item.body}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

export function IntelligencePanel() {
  return (
    <section
      className="s-dark section-pad grid-bg"
      id="intelligence"
      data-screen-label="Intelligence"
    >
      <div
        className="glow"
        style={{
          width: 480,
          height: 480,
          background: 'var(--violet)',
          right: -120,
          top: '10%',
          opacity: 0.16,
        }}
      />
      <div className="wrap">
        <div className="ai-grid">
          <div className="reveal">
            <span className="eyebrow" style={{ color: 'var(--violet-ink, var(--violet))' }}>
              {LANDING_COPY.intelligence.eyebrow}
            </span>
            <h2 className="h2" style={{ marginTop: 18 }}>
              {LANDING_COPY.intelligence.heading}
            </h2>
            <div className="ai-points">
              {INTELLIGENCE_POINTS.map((point) => (
                <div className="ai-point" key={point.title}>
                  <span className="b" />
                  <div>
                    <h3>{point.title}</h3>
                    <p>{point.body}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
          <div
            aria-label="Example intelligence signals"
            className="signal-panel reveal d1"
            id="signalPanel"
            role="list"
          >
            <p className="sr-only">
              These are research-support prompts for qualified review, not autonomous medical advice
              or efficacy claims.
            </p>
            <div className="signal-head">
              <span className="t">{SIGNAL_PANEL.title}</span>
              <span className="live">{SIGNAL_PANEL.status}</span>
            </div>
            <div className="bars" id="signalBars">
              {SIGNAL_BARS.map((bar) => (
                <div className="bar-row" key={bar.label} role="listitem">
                  <span className="lbl">{bar.label}</span>
                  <div className="bar-track">
                    <div
                      className={`bar-fill${'variant' in bar && bar.variant === 'violet' ? ' violet' : ''}`}
                      data-v={bar.value}
                      style={{ width: 0 }}
                    />
                  </div>
                  <span className="val">{bar.value}%</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

export function TrustGrid() {
  return (
    <section
      className="s-light section-pad"
      id="research"
      data-screen-label="Research & compliance"
    >
      <div className="wrap">
        <SectionHead
          eyebrow={LANDING_COPY.research.eyebrow}
          heading={LANDING_COPY.research.heading}
          body={LANDING_COPY.research.body}
        />
        <div className="trust-grid" id="trustGrid">
          {RESEARCH_TRUST_CARDS.map((item, index) => (
            <div className={`trust-card reveal d${index % 4}`} key={item.title}>
              <div className="ic">
                <Icon name={item.icon} />
              </div>
              <h3>{item.title}</h3>
              <p>{item.body}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

export function Partners() {
  return (
    <section className="s-dark section-pad grid-bg" id="partners" data-screen-label="Partners">
      <div className="wrap">
        <SectionHead
          eyebrow={LANDING_COPY.partners.eyebrow}
          heading={LANDING_COPY.partners.heading}
          body={LANDING_COPY.partners.body}
        />
        <div className="aud-grid">
          {PARTNER_TILES.map((partner, index) => (
            <div className={`aud reveal${index ? ` d${index}` : ''}`} key={partner.tag}>
              <span className="tag">{partner.tag}</span>
              <h3>{partner.title}</h3>
              <p>{partner.body}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

export function FinalCTA() {
  return (
    <section className="s-dark section-pad final grid-bg" data-screen-label="Final CTA">
      <div
        className="glow"
        style={{
          width: 560,
          height: 560,
          background: 'var(--accent)',
          left: '50%',
          top: '50%',
          transform: 'translate(-50%,-50%)',
          opacity: 0.16,
        }}
      />
      <div className="wrap">
        <span className="eyebrow reveal" style={{ justifyContent: 'center' }}>
          {LANDING_COPY.finalCta.eyebrow}
        </span>
        <h2 className="display reveal d1" style={{ marginTop: 20 }}>
          {LANDING_COPY.finalCta.heading}
        </h2>
        <p className="lede reveal d2">{LANDING_COPY.finalCta.body}</p>
        <div className="final-cta reveal d3">
          <a href="#contact" className="btn btn-primary">
            Start the Conversation
            <ArrowIcon />
          </a>
        </div>
      </div>
    </section>
  );
}

export function ContactForm() {
  const [submitted, setSubmitted] = useState(false);
  const [errors, setErrors] = useState<{ name?: string; email?: string }>({});

  const handleSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const form = new FormData(event.currentTarget);
    const name = String(form.get('name') ?? '').trim();
    const email = String(form.get('email') ?? '').trim();
    const nextErrors: { name?: string; email?: string } = {};

    if (!name) nextErrors.name = 'Name is required.';
    if (!email) {
      nextErrors.email = 'Email is required.';
    } else if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(email)) {
      nextErrors.email = 'Enter a valid email address.';
    }

    setErrors(nextErrors);
    if (Object.keys(nextErrors).length === 0) {
      setSubmitted(true);
      event.currentTarget.reset();
    }
  };

  return (
    <form className="form reveal d1" id="leadForm" noValidate onSubmit={handleSubmit}>
      <div className="field row2">
        <div>
          <label htmlFor="f-name">
            Name <span className="req">*</span>
          </label>
          <input
            id="f-name"
            name="name"
            type="text"
            autoComplete="name"
            required
            placeholder={CONTACT_DATA.form.fields.name.placeholder}
            aria-invalid={Boolean(errors.name)}
            aria-describedby={errors.name ? 'name-error' : undefined}
            style={errors.name ? { borderColor: 'oklch(0.6 0.18 25)' } : undefined}
          />
          {errors.name && <span id="name-error">{errors.name}</span>}
        </div>
        <div>
          <label htmlFor="f-email">
            Email <span className="req">*</span>
          </label>
          <input
            id="f-email"
            name="email"
            type="email"
            autoComplete="email"
            required
            placeholder={CONTACT_DATA.form.fields.email.placeholder}
            aria-invalid={Boolean(errors.email)}
            aria-describedby={errors.email ? 'email-error' : undefined}
            style={errors.email ? { borderColor: 'oklch(0.6 0.18 25)' } : undefined}
          />
          {errors.email && <span id="email-error">{errors.email}</span>}
        </div>
      </div>
      <div className="field row2">
        <div>
          <label htmlFor="f-org">Organization</label>
          <input
            id="f-org"
            name="organization"
            type="text"
            autoComplete="organization"
            placeholder={CONTACT_DATA.form.fields.organization.placeholder}
          />
        </div>
        <div>
          <label htmlFor="f-role">Role</label>
          <input
            id="f-role"
            name="role"
            type="text"
            autoComplete="organization-title"
            placeholder={CONTACT_DATA.form.fields.role.placeholder}
          />
        </div>
      </div>
      <div className="field">
        <label htmlFor="f-interest">Interest</label>
        <select id="f-interest" name="interest" autoComplete="off" defaultValue="">
          <option value="">{CONTACT_DATA.form.fields.interest.placeholder}</option>
          {CONTACT_DATA.form.interestOptions.map((option) => (
            <option key={option}>{option}</option>
          ))}
        </select>
      </div>
      <div className="field">
        <label htmlFor="f-message">Message</label>
        <textarea
          id="f-message"
          name="message"
          autoComplete="off"
          placeholder={CONTACT_DATA.form.fields.message.placeholder}
        />
      </div>
      <button type="submit" className="btn btn-primary">
        {CONTACT_DATA.form.submitLabel}
        <ArrowIcon />
      </button>
      <p className="form-note">{CONTACT_DATA.form.note}</p>
      <div
        className={`form-success${submitted ? ' show' : ''}`}
        id="formSuccess"
        role="status"
        aria-live="polite"
      >
        {submitted && (
          <>
            <div className="check">
              <svg viewBox="0 0 24 24" aria-hidden="true">
                <path d="M5 13l4 4L19 7" />
              </svg>
            </div>
            <h3>{CONTACT_DATA.form.success.title}</h3>
            <p>{CONTACT_DATA.form.success.body}</p>
          </>
        )}
      </div>
    </form>
  );
}

export function ContactSection() {
  return (
    <section
      className="s-dark section-pad"
      id="contact"
      data-screen-label="Contact"
      style={{ borderTop: '1px solid var(--hair-dark)' }}
    >
      <div className="wrap contact-grid">
        <div className="contact-aside reveal">
          <span className="eyebrow">{LANDING_COPY.contact.eyebrow}</span>
          <h2 className="h2" style={{ marginTop: 18 }}>
            {LANDING_COPY.contact.heading}
          </h2>
          <p className="lede">{LANDING_COPY.contact.body}</p>
          <div className="contact-meta">
            <a href={`mailto:${CONTACT_DATA.email}`}>
              <svg viewBox="0 0 24 24" aria-hidden="true">
                <rect x="3" y="5" width="18" height="14" rx="2" />
                <path d="M3 7l9 6 9-6" />
              </svg>{' '}
              {CONTACT_DATA.email}
            </a>
            <div>
              <svg viewBox="0 0 24 24" aria-hidden="true">
                <path d="M12 3l7 3v5c0 5-3 8-7 10-4-2-7-5-7-10V6z" />
              </svg>{' '}
              {CONTACT_DATA.privacyLine}
            </div>
          </div>
        </div>
        <ContactForm />
      </div>
    </section>
  );
}

export function Footer() {
  return (
    <footer className="s-dark footer" data-screen-label="Footer">
      <div className="wrap">
        <div className="footer-top">
          <div>
            <a className="brand" href="#top">
              <BrandMark />
              <BrandText />
            </a>
            <p className="positioning">{LANDING_COPY.brand.positioning}</p>
          </div>
          <div className="footer-links">
            {FOOTER_LINK_COLUMNS.map((column) => (
              <div className="footer-col" key={column.title}>
                <h4>{column.title}</h4>
                {column.links.map((link) => (
                  <a href={link.href} key={`${column.title}-${link.label}`}>
                    {link.label}
                  </a>
                ))}
              </div>
            ))}
          </div>
        </div>
        <div className="footer-bottom">
          <span>{FOOTER_COPY.copyright}</span>
          <span>{productConfig.footerLegalLine}</span>
        </div>
      </div>
    </footer>
  );
}

export function StickyCTA() {
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const onScroll = () => {
      const hero = document.querySelector<HTMLElement>('.hero');
      const contact = document.getElementById('contact');
      const y = window.scrollY || window.pageYOffset;
      const pastHero = hero ? y > hero.offsetHeight * 0.7 : y > 500;
      const nearForm = contact
        ? contact.getBoundingClientRect().top < window.innerHeight * 0.9
        : false;
      setVisible(pastHero && !nearForm);
    };
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  return (
    <div className={`sticky-cta${visible ? ' show' : ''}`} id="stickyCta">
      <a href={STICKY_CTA.href} className="btn btn-primary">
        {STICKY_CTA.label}
      </a>
    </div>
  );
}

export function LandingPage() {
  useForcedLightTheme();
  useLandingEnhancements();

  return (
    <>
      <LandingNav />
      <main id="top">
        <Hero />
        <ProblemFragments />
        <CardGrid />
        <ClinicianAppShowcase />
        <ConnectiveLayer />
        <LoopSection />
        <ModulesGrid />
        <IntelligencePanel />
        <TrustGrid />
        <FinalCTA />
        <ContactSection />
      </main>
      <Footer />
      <StickyCTA />
    </>
  );
}

export default LandingPage;
